import { Box } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemTabs } from "../../../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import type { SystemTab } from "../../../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/utils";
import { LabeledField } from "../../../../components/UIToolkit/molecules/LabeledField";
import { useFormatPersonName } from "../../../../hooks/useNameFormat";
import { formatIsoDate, formatIsoDateTime } from "../../../../utils/dateUtils";
import type { ExternalEmployee } from "../../../../utils/models";
import { CommonHeader } from "../util/constants";

type Props = { externalEmployees: ExternalEmployee[] };

const formatAddress = (e: ExternalEmployee): string => {
	const street = [e.street, e.house_number].filter(Boolean).join(" ");
	const parts = [street, e.city, e.country, e.zipcode].filter(Boolean);
	return parts.join(", ");
};

/**
 * A row's full identity — `external_employee`'s primary key is (id, source, sync_key), so `id` alone
 * (or even source + id) can repeat across the rows of one nurse and would resolve to the wrong row.
 */
const rowKey = (e: ExternalEmployee): string => `${e.id}|${e.source}|${e.sync_key}`;

const baseLabel = (e: ExternalEmployee): string => `${e.source} (${e.id})`;

export const Integrations: FC<Props> = ({ externalEmployees }) => {
	const { t } = useTranslation();
	const formatPersonName = useFormatPersonName();
	const [activeKey, setActiveKey] = useState<string>(externalEmployees[0] ? rowKey(externalEmployees[0]) : "");

	if (externalEmployees.length === 0) return null;

	/** Same source + id under two sync keys renders two identical labels — spell the sync key out only then. */
	const ambiguousLabels = new Set(externalEmployees.map(baseLabel).filter((label, i, all) => all.indexOf(label) !== i));

	const tabs: SystemTab<string>[] = externalEmployees.map((e) => ({
		label: ambiguousLabels.has(baseLabel(e)) ? `${baseLabel(e)} · ${e.sync_key}` : baseLabel(e),
		value: rowKey(e),
	}));

	const selected = externalEmployees.find((e) => rowKey(e) === activeKey) ?? externalEmployees[0];

	return (
		<Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
				<CommonHeader>{t("profile.integrations.title")}</CommonHeader>
				<SystemTabs tabs={tabs} currentTab={rowKey(selected)} onTabChange={setActiveKey} dataTestId="profile-integrations-tabs" />
			</Box>

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
					rowGap: "16px",
					columnGap: "24px",
				}}
			>
				<LabeledField label={t("profile.integrations.field.name")} value={formatPersonName({ firstName: selected.first_name, lastName: selected.last_name })} />
				<LabeledField label={t("profile.integrations.field.source")} value={selected.source} />
				<LabeledField label={t("profile.integrations.field.externalId")} value={selected.id} />
				<LabeledField label={t("profile.integrations.field.role")} value={selected.role} />
				<LabeledField label={t("profile.integrations.field.rawRole")} value={selected.raw_role} />
				<LabeledField label={t("profile.integrations.field.wage")} value={`${selected.wage} (${selected.wage_type})`} />
				<LabeledField label={t("profile.integrations.field.rawWage")} value={selected.raw_wage} />
				<LabeledField label={t("profile.integrations.field.employmentType")} value={selected.employment_type} />
				<LabeledField label={t("profile.integrations.field.rawEmploymentType")} value={selected.raw_employment_type} />
				<LabeledField label={t("profile.integrations.field.email")} value={selected.email} />
				<LabeledField label={t("profile.integrations.field.rawEmail")} value={selected.raw_email} />
				<LabeledField label={t("profile.integrations.field.phoneNumber")} value={selected.phone_number} />
				<LabeledField label={t("profile.integrations.field.rawPhoneNumber")} value={selected.raw_phone_number} />
				<LabeledField label={t("profile.integrations.field.hireDate")} value={formatIsoDate(selected.hire_date)} />
				<LabeledField label={t("profile.integrations.field.terminationDate")} value={formatIsoDate(selected.termination_date)} />
				<LabeledField label={t("profile.integrations.field.birthDate")} value={formatIsoDate(selected.birth_date)} />
				<LabeledField label={t("profile.integrations.field.agencyName")} value={selected.agency_name} />
				<LabeledField label={t("profile.integrations.field.unionCode")} value={selected.union_code} />
				<LabeledField label={t("profile.integrations.field.refId")} value={selected.ref_id} />
				<LabeledField label={t("profile.integrations.field.pbjId")} value={selected.pbj_id} />
				<LabeledField label={t("profile.integrations.field.departmentId")} value={selected.department_id} />
				<LabeledField label={t("profile.integrations.field.syncKey")} value={selected.sync_key} />
				<LabeledField label={t("profile.integrations.field.address")} value={formatAddress(selected)} />
				<LabeledField label={t("profile.integrations.field.active")} value={selected.active ? t("yes") : t("no")} />
				<LabeledField label={t("profile.integrations.field.rawStatus")} value={selected.raw_employee_status} />
				<LabeledField label={t("profile.integrations.field.createdAt")} value={formatIsoDateTime(selected.created_at)} />
				<LabeledField label={t("profile.integrations.field.updatedAt")} value={formatIsoDateTime(selected.updated_at)} />
			</Box>
		</Box>
	);
};
